import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import config from "../../config";
import { useMasterPassword } from "../../components/MasterPasswordDialog/MasterPasswordContext";

const omitNullish = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    const out = {};
    Object.keys(obj).forEach((k) => {
        const v = obj[k];
        if (v !== null && v !== undefined) {
            out[k] = v;
        }
    });
    return out;
};

const fetchTableOrders = (tableId) => {
    const params = {
        model: `Restaurant Object`,
        name: tableId,
        method: `orders_list`,
        args: "{}",
    };
    return axios.get(`method/frappe_helper.api.call`, { params });
};

export const useTableOrders = (
    tableId,
    extraDescriptor = "",
    onSuccess,
    onError
) => {
    return useQuery(
        ["getTableOrders" + extraDescriptor, tableId],
        async () => {
            const data = await fetchTableOrders(tableId);
            return data.data.message;
        },
        {
            onSuccess,
            onError,
            enabled: !!tableId,
            cacheTime: 0,
            refetchOnMount: true,
        }
    );
};

export const useTableOrdersLazy = (
    tableId,
    extraDescriptor = "",
    onSuccess,
    onError
) => {
    return useQuery(
        ["getTableOrders" + extraDescriptor, tableId],
        async () => {
            const data = await fetchTableOrders(tableId);
            return data.data.message;
        },
        {
            onSuccess,
            onError,
            enabled: false,
        }
    );
};

const createTableOrder = (tableId, formData) => {
    const params = {
        model: `Restaurant Object`,
        name: tableId,
        method: `add_order`,
        args: '{"client":"obj_69b9-9d-463-847-91b8f"}',
    };
    return axios.post(`method/frappe_helper.api.call`, params);
};

export const useCreateTableOrder = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ tableId, formData }) => {
            const data = await createTableOrder(tableId, formData);
            return data;
        },
        {
            onSuccess: (data, { tableId }) => {
                // Refresh the query for the table order items.
                queryClient.invalidateQueries(["getTableOrders", tableId]);
                queryClient.refetchQueries(["getTableOrders", tableId]);
            },
        }
    );
};

const addNewOrder = ({
    orderItems,
    table,
    customer,
    address,
    address_line1,
    address_line2,
    mobile_no,
    city,
    delivery_provider,
    masterPassword,
    dinners,
    dinners_children,
    dinners_infants,
    notes,
    estimated_preparation_time,
    designated_number,
    sendToKitchen = true,
    is_draft,
    draft_name,
    call_center_order,
}) => {
    const args = omitNullish({
        order_items: orderItems,
        send_to_kitchen: !!sendToKitchen,
        customer,
        address,
        address_line1,
        address_line2,
        mobile_no,
        city,
        delivery_provider,
        dinners,
        dinners_children,
        dinners_infants,
        notes,
        estimated_preparation_time,
        designated_number,
        is_draft,
        draft_name,
        call_center_order,
    });

    const params = {
        model: "Restaurant Object",
        name: table,
        method: "add_order_with_customer",
        args: JSON.stringify(args),
    };

    if (masterPassword) {
        params.master_password = masterPassword;
    }

    return axios.post(
        masterPassword
            ? "method/restaurant_management.api.escalate_privilege"
            : "method/frappe_helper.api.call",
        params
    );
};

export const useAddNewOrder = () => {
    const { showMasterPasswordDialog } = useMasterPassword();

    const mutation = useMutation(
        async ({
            orderItems,
            table,
            customer,
            address,
            address_line1,
            address_line2,
            mobile_no,
            city,
            delivery_provider,
            masterPassword,
            dinners,
            dinners_children,
            dinners_infants,
            notes,
            estimated_preparation_time,
            designated_number,
            call_center_order,
        }) => {
            const { data } = await addNewOrder({
                orderItems,
                table,
                customer,
                address,
                address_line1,
                address_line2,
                mobile_no,
                city,
                delivery_provider,
                masterPassword,
                dinners,
                dinners_children,
                dinners_infants,
                notes,
                estimated_preparation_time,
                designated_number,
                sendToKitchen: true,
                call_center_order,
            });
            return data;
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                console.log("Master password dialog cancelled");
                return;
            }
        } else {
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

export const useAddNewOrderDraft = () => {
    const { showMasterPasswordDialog } = useMasterPassword();

    const mutation = useMutation(
        async ({
            orderItems,
            table,
            customer,
            address,
            address_line1,
            address_line2,
            mobile_no,
            city,
            delivery_provider,
            masterPassword,
            dinners,
            dinners_children,
            dinners_infants,
            notes,
            estimated_preparation_time,
            designated_number,
            call_center_order,
        }) => {
            const { data } = await addNewOrder({
                orderItems,
                table,
                customer,
                address,
                address_line1,
                address_line2,
                mobile_no,
                city,
                delivery_provider,
                masterPassword,
                dinners,
                dinners_children,
                dinners_infants,
                notes,
                estimated_preparation_time,
                designated_number,
                sendToKitchen: false,
                is_draft: true,
                call_center_order,
            });
            return data;
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                console.log("Master password dialog cancelled");
                return;
            }
        } else {
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

const pushOrderItems = ({ orderItems, order, masterPassword, sendToKitchen = true, doctype = "Table Order" }) => {
    const args = omitNullish({
        order_items: orderItems,
        send_to_kitchen: !!sendToKitchen,
    });

    const params = {
        model: doctype,
        name: order.name,
        method: "_push_items",
        args: JSON.stringify(args),
    };
    if (masterPassword) {
        params.master_password = masterPassword;
    }
    return axios.post(
        masterPassword
            ? "method/restaurant_management.api.escalate_privilege"
            : "method/frappe_helper.api.call",
        params
    );
};

const setOrderReadyTime = ({ order }) => {
    const params = {
        model: "Table Order",
        name: order.name,
        method: "set_order_ready_time",
        args: "{}",
    };
    return axios.post("method/frappe_helper.api.call", params);
};

export const useSetOrderReadyTime = () => {
    return useMutation(async ({ order }) => {
        const { data } = await setOrderReadyTime({ order });
        return data;
    });
};

export const usePushOrderItems = () => {
    const { showMasterPasswordDialog } = useMasterPassword();

    const mutation = useMutation(
        async ({ orderItems, order, masterPassword }) => {
            const { data } = await pushOrderItems({
                orderItems,
                order,
                masterPassword,
                sendToKitchen: true,
            });
            return data;
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                // Show dialog and wait for master password
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                // Execute with master password
                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                // User cancelled the dialog
                console.log("Master password dialog cancelled");
                return;
            }
        } else {
            // Execute directly without master password
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

export const usePushOrderItemsDraft = () => {
    const { showMasterPasswordDialog } = useMasterPassword();

    const mutation = useMutation(
        async ({ orderItems, order, masterPassword }) => {
            const { data } = await pushOrderItems({
                orderItems,
                order,
                masterPassword,
                sendToKitchen: false,
                doctype: "Draft Table Order",
            });
            return data;
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                // Show dialog and wait for master password
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                // Execute with master password
                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                // User cancelled the dialog
                console.log("Master password dialog cancelled");
                return;
            }
        } else {
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

/**
 * Convert a Draft Table Order into a real Table Order.
 *
 * Calls `add_order_with_customer` on the Restaurant Object (table) with
 * `is_draft=false` and `draft_name` set to the draft doc name.  The backend
 * creates a new Table Order with the provided items and marks the draft as
 * "Attending".
 */
const sendDraftAsOrder = ({
    table,
    draftName,
    orderItems,
    customer,
    address,
    address_line1,
    address_line2,
    mobile_no,
    dinners,
    dinners_children,
    dinners_infants,
    notes,
    estimated_preparation_time,
    designated_number,
    delivery_provider,
    masterPassword,
    call_center_order,
}) => {
    const args = omitNullish({
        order_items: orderItems,
        send_to_kitchen: true,
        is_draft: false,
        draft_name: draftName,
        customer,
        address,
        address_line1,
        address_line2,
        mobile_no,
        dinners,
        dinners_children,
        dinners_infants,
        notes,
        estimated_preparation_time,
        designated_number,
        delivery_provider,
        call_center_order,
    });

    const params = {
        model: "Restaurant Object",
        name: table,
        method: "add_order_with_customer",
        args: JSON.stringify(args),
    };

    if (masterPassword) {
        params.master_password = masterPassword;
    }

    return axios.post(
        masterPassword
            ? "method/restaurant_management.api.escalate_privilege"
            : "method/frappe_helper.api.call",
        params
    );
};

export const useSendTableOrder = () => {
    return useMutation(async (payload) => {
        const { data } = await sendDraftAsOrder(payload);
        return data;
    });
};

const deleteTableOrder = (tableId, orderName) => {
    const params = {
        model: `Table Order`,
        name: orderName,
        method: `_delete`,
    };
    return axios.post(`method/frappe_helper.api.call`, params);
};

export const useDeleteTableOrder = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ tableId, orderName }) => {
            const data = await deleteTableOrder(tableId, orderName);
            return data;
        },
        {
            onSuccess: (data, { tableId }) => {
                // Refresh the query for the table order items.
                queryClient.refetchQueries(["getTableOrders", tableId]);
            },
        }
    );
};

const reserveTableCall = (tableId, customerName) => {
    const params = {
        tableName: tableId,
        customerName: customerName,
    };
    return axios.post(`method/restaurant_management.api.reserve_table`, params);
};

export const useReserveTableData = () => {
    return useMutation(async ({ tableId, customerName }) => {
        const data = await reserveTableCall(tableId, customerName);
        return data;
    });
};

const assignCustomerToOrderItem = (orderId, payload) => {
    const params = {
        desk_form: `restaurant-order-customer`,
        doc_name: orderId,
        data: JSON.stringify(payload),
    };
    return axios.post(
        `method/frappe_helper.frappe_helper.doctype.desk_form.desk_form.accept`,
        params
    );
};

export const useAssignCustomerToTableData = (orderId, payload) => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, payload }) => {
            const data = await assignCustomerToOrderItem(orderId, payload);
            return data;
        },
        {
            onSuccess: (data, { orderId, payload }) => {
                // we will refresh things.
                const { table } = payload;
                if (table) {
                    queryClient.invalidateQueries(["getTableOrders", table]);
                    queryClient.refetchQueries(["getTableOrders", table]);
                }

                queryClient.invalidateQueries(
                    "getTableTakeAwayOrder" + config.takeaway_table_identity
                ); // Replace 'getTableOrders' with your query key
                queryClient.refetchQueries(
                    "getTableTakeAwayOrder" + config.takeaway_table_identity
                ); // Replace 'getTableOrders' with your query key
            },
        }
    );
};

const moveTableOrders = (orders, destTable) => {
    const params = {
        orders: JSON.stringify(orders),
        destinationTable: destTable,
    };
    return axios.post(
        `method/restaurant_management.api.move_table_orders`,
        params
    );
};

export const useMoveTableOrdersData = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orders, destTable }) => {
            const data = await moveTableOrders(orders, destTable);
            return data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["getTableOrdersData"]);
            },
        }
    );
};

const fetchInvoiceData = ({ orderId, entry_items, discount_percentage, employee }) => {
    const args = {
        entry_items,
        discount_percentage: discount_percentage,
    };
    if (employee) {
        args.employee = employee;
    }
    const params = {
        model: `Table Order`,
        name: orderId,
        method: `get_invoice`,
        args: JSON.stringify(args),
    };
    return axios.post(`method/frappe_helper.api.call`, params);
};

export const useGetInvoiceData = ({
    orderId,
    entry_items,
    discount_percentage = 0,
    employee,
}) => {
    return useQuery(
        [`getInvoiceData_${orderId}`],
        async () => {
            const data = await fetchInvoiceData({
                orderId,
                entry_items,
                discount_percentage,
                employee,
            });
            return data.data.message;
        },
        {
            enabled:
                !!orderId &&
                !!entry_items &&
                Object.keys(entry_items).length > 0,
            cacheTime: 0,
            refetchOnMount: true,
        }
    );
};

export const useFetchGetInvoiceData = () => {
    return useMutation(
        async ({ orderId, entry_items, discount_percentage, employee }) => {
            const args = {
                entry_items,
                discount_percentage,
            };
            if (employee) {
                args.employee = employee;
            }
            const data = await axios.post(`method/frappe_helper.api.call`, {
                model: `Table Order`,
                name: orderId,
                method: `get_invoice`,
                args: JSON.stringify(args),
            });
            const result = data.data.message;
            if (data.data._server_messages) {
                result._server_messages = data.data._server_messages;
            }
            return result;
        },
        {
            onSuccess: (data) => {
                return data;
            },
        }
    );
};

export const useHandleStaffMeal = () => {
    return useMutation(async ({ table_order, mode_of_payment, employee }) => {
        const payload = {
            table_order,
            mode_of_payment,
            employee,
        };
        const { data } = await axios.post(
            "method/restaurant_management.api.handle_staff_meal",
            payload
        );
        return data?.message ?? data;
    });
};

export const useFetchTableOrder = () => {
    return useMutation(async ({ name }) => {
        const params = {
            doctype: "Table Order",
            name,
        };
        const { data } = await axios.get("method/frappe.client.get", { params });
        return data?.message;
    });
};
